import {
  TriangleUpIcon,
  TriangleDownIcon,
  AddIcon,
  MinusIcon,
} from '@chakra-ui/icons';
import {
  HStack,
  Heading,
  Collapse,
  VStack,
  Text,
  Input,
  NumberInput,
  NumberInputField,
  NumberIncrementStepper,
  NumberDecrementStepper,
  NumberInputStepper,
  FormControl,
  IconButton,
  FormLabel,
  Button,
  Select,
  CheckboxGroup,
  Checkbox,
  Box,
} from '@chakra-ui/react';
import { TimelineContextProvider } from 'components/GameTimeline/TimelineContext';
import { setHours } from 'date-fns';
import { useEffect, useState } from 'react';
import { Schedule } from 'types';
import { TimeSlotWithCourts } from 'types/dto';
import {
  useGenerateScheduleMutation,
  useGetLeaguesForEventQuery,
  useGetCourtsForEventQuery,
  useImportScheduleMutation,
  useSaveScheduleMutation,
  useUpdateLeagueEventMutation,
} from '../../../app/api';
import Card from '../../../components/Card/Card';
import { GameTimeline } from '../../../components/GameTimeline/GameTimeline';
import { TimeInput } from '../../../components/TimeInput';
import { useEventContext } from '../../../contexts/EventContext';

interface ScheduleCreationSectionProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  eventId: number;
}

export const ScheduleCreationSection: React.FC<
  ScheduleCreationSectionProps
> = ({ isOpen, onClose, onOpen, eventId }) => {
  const [transformedGames, setTransformedGames] = useState<
    Schedule | undefined
  >();
  const { currentEvent } = useEventContext();
  const { data: leagues, refetch: refetchLeagues } =
    useGetLeaguesForEventQuery(eventId);
  const { data: courts } = useGetCourtsForEventQuery(eventId);
  const [timeslots, setTimeslots] = useState<TimeSlotWithCourts[]>([]);
  const [selectedDates, setSelectedDates] = useState<[Date, Date]>(
    currentEvent
      ? [new Date(currentEvent.startDate), new Date(currentEvent.endDate)]
      : [new Date(), new Date()]
  );
  const [gameDuration, setGameDuration] = useState<number>(20);
  const [timeBetweenGames, setTimeBetweenGames] = useState<number>(5);
  const [updateLeagueEvent] = useUpdateLeagueEventMutation();
  const [generateSchedule, { data: generatedGames }] =
    useGenerateScheduleMutation();
  const [saveSchedule] = useImportScheduleMutation();

  const onTimeslotChange = (
    newTime: Date,
    index: number,
    startOrEnd: 'start' | 'end'
  ) => {
    const timeslot = timeslots[index];
    const { startTime, endTime, courtIds } = timeslot;
    let time = startOrEnd === 'start' ? startTime : endTime;
    if (isNaN(time.getTime())) {
      time = new Date(currentEvent?.startDate || Date.now());
    }
    setTimeslots([
      ...timeslots.slice(0, index),
      {
        startTime: startOrEnd === 'start' ? newTime : startTime,
        endTime: startOrEnd === 'end' ? newTime : endTime,
        courtIds
      },
      ...timeslots.slice(index + 1),
    ]);
  };

  const onCourtsChange = (selectedCourtIds: number[], index: number) => {
    const timeslot = timeslots[index];
    setTimeslots([
      ...timeslots.slice(0, index),
      { ...timeslot, courtIds: selectedCourtIds },
      ...timeslots.slice(index + 1),
    ]);
  };

  const handleGeneratePlan = () => {
    if (currentEvent && timeslots.length > 0) {
      generateSchedule({
        eventId: currentEvent.id,
        timeslots,
        gameDuration,
        timeBetweenGames,
      });
    }
  };

  useEffect(() => {
    if (generatedGames) {
      setTransformedGames({
        ...generatedGames,
        days: generatedGames.days.map((day, dayIndex) => ({
          ...day,
          games: day.games.map((game, gameIndex) => ({
            ...game,
            id: parseInt(`${dayIndex + 1}${gameIndex}`),
          })),
        })),
      });
    }
  }, [generatedGames]);

  return (
    <Card>
      <HStack w="100%">
        <Heading as="h3" size="md">
          Schedule Creation
        </Heading>
        {/* <Button>Batch Create</Button> */}
        {isOpen ? (
          <TriangleUpIcon onClick={onClose} />
        ) : (
          <TriangleDownIcon onClick={onOpen} />
        )}
      </HStack>
      <Collapse in={isOpen}>
        <VStack>
          {/* <RangeDatepicker
            selectedDates={selectedDates}
            onDateChange={(dates) => setSelectedDates([dates[0], dates[1]])}
          /> */}
          <HStack>
            <Heading as="h4" size="md">
              Add Timeslots
            </Heading>
            <IconButton
              size="sm"
              icon={<AddIcon />}
              aria-label="add-timeslot"
              onClick={() =>
                setTimeslots([
                  ...timeslots,
                  {
                    startTime: setHours(
                      new Date(currentEvent?.startDate ?? Date.now()),
                      8
                    ),
                    endTime: setHours(
                      new Date(currentEvent?.startDate ?? Date.now()),
                      17
                    ),
                    courtIds: courts?.map(c => c.id) || []
                  },
                ])
              }
            />
          </HStack>
          <VStack width="100%">
            {timeslots.map((timeslot, index) => (
              <VStack key={index} width="100%" align="stretch" spacing={2} border="1px" borderColor="gray.200" borderRadius="md" p={2}>
                <HStack>
                  <FormControl>
                    <TimeInput
                      placeholder="From"
                      type="time"
                      date={timeslot.startTime}
                      onTimeChange={(newTime) =>
                        onTimeslotChange(newTime, index, 'start')
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <TimeInput
                      placeholder="To"
                      type="time"
                      date={timeslot.endTime}
                      onTimeChange={(newTime) =>
                        onTimeslotChange(newTime, index, 'end')
                      }
                    />
                  </FormControl>
                  <IconButton
                    size="sm"
                    icon={<MinusIcon />}
                    aria-label="remove-timeslot"
                    onClick={() =>
                      setTimeslots([
                        ...timeslots.slice(0, index),
                        ...timeslots.slice(index + 1),
                      ])
                    }
                  />
                </HStack>
                <FormControl>
                  <FormLabel fontSize="sm">Select Courts</FormLabel>
                  <CheckboxGroup
                    value={timeslot.courtIds.map(String)}
                    onChange={(values) => onCourtsChange(values.map(Number), index)}
                  >
                    <HStack wrap="wrap">
                      {courts?.slice().sort((a, b) => a.name.localeCompare(b.name)).map((court) => (
                        <Checkbox key={court.id} value={String(court.id)}>
                          {court.name}
                        </Checkbox>
                      ))}
                    </HStack>
                  </CheckboxGroup>
                </FormControl>
              </VStack>
            ))}
          </VStack>
          <Heading size="sm">Number of games per league</Heading>
          {leagues?.map((league) => (
            <HStack key={league.id}>
              <Text>{league.name}</Text>
              <NumberInput
                value={league.numGames}
                onChange={(val) => {
                  if (currentEvent) {
                    updateLeagueEvent({
                      eventId: currentEvent.id,
                      leagueId: league.id,
                      leagueEvent: { ...league, numGames: parseInt(val) },
                    }).then(() => refetchLeagues());
                  }
                }}
                size="sm"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </HStack>
          ))}
          <FormControl>
            <FormLabel>Game time (minutes)</FormLabel>
            <NumberInput
              value={gameDuration}
              onChange={(val) => setGameDuration(parseInt(val))}
              size="sm"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel>Time between Games (minutes)</FormLabel>
            <NumberInput
              value={timeBetweenGames}
              onChange={(val) => setTimeBetweenGames(parseInt(val))}
              size="sm"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
          {transformedGames && (
            <GameTimeline
              schedule={transformedGames}
              onGameSelect={() => {}}
              editing={true}
              editingSchedule={transformedGames}
            />
          )}
          <HStack>
            <Button onClick={handleGeneratePlan}>
              Generate Plan
            </Button>
            <Button
              isDisabled={!generatedGames || !currentEvent}
              onClick={() => {
                if (!!generatedGames && currentEvent) {
                  saveSchedule({
                    schedule: generatedGames,
                    eventId: currentEvent.id,
                  }).then(() => onClose());
                }
              }}
            >
              Save Schedule
            </Button>
          </HStack>
        </VStack>
      </Collapse>
    </Card>
  );
};
